import React, {useEffect, useRef, useState} from 'react';
import useTranslation from '../../../hooks/useTranslation';
import { useSelector } from 'react-redux';
import { getUserPageAuth } from '../../../utils/page-auth';
import { Helmet } from 'react-helmet';
import * as XLSX from 'xlsx';
import { convertBigNumToDecimalString, convertDecimalToBigNum } from '../../../utils/convert-decimal-to-big-num';
import { Visible } from '../../../components';
import axios from 'axios';
import config from '../../../config/config';
import cookies from 'react-cookies';
import ApiClient from '../../../redux/models/src/ApiClient';
import Notification from '../../../components/Notification/Notification';
import { getErrorMessage } from '../../../utils/serverErrorCheck';
import FetchLoading from "../../../components/Loading/FetchLoading";
import BtnIcons from "../../../api/images/btnIcons";
import ExcelPostModal from "../../../components/modal/ExcelPostModal";

function FileBasedPostMain() {
  // -- component const --//
  const title = "엑셀기반 우편"; // "admin.user.fileBasedPost.title"

  const { t } = useTranslation();

  const apiURL = config.API_URL;
  const accessToken = cookies.load('token');
  const apiClient = new ApiClient();
  const headerData = apiClient.defaultHeaders;

  const user = useSelector((state) => state.authReducer.user);

  // -- state --//
  const [loading, setLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);

  const [readOnly, setReadOnly] = useState(true);

  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  // 모달 상태 관리
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 지갑 번호 (우편: 10번) 고정
  const [walletNo, setWalletNo] = useState(10);

  // 전송 주소 설정
  const [address, setAddress] = useState('0xe1942320bdB0576F782478AB53F2b447C65DcE24');

  const [initialPer, setInitialPer] = useState(0);
  const [initialXper, setInitialXper] = useState(0);

  // 발송 실패 목록
  const [failList, setFailList] = useState([]);

  // 우편 발송 버튼 제어
  const [submitPermission, setSubmitPermission] = useState(false);

  // -- func & handler --//
  // 파일 업로드
  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if(!file){
      setNotificationMessage("엑셀 파일이 등록되지 않았습니다.");
      setShowNotification(true);
      setSubmitPermission(false);
      return;
    }

    const allowedExtensions = /(\.xlsx|\.xls|\.csv)$/i;

    if (!allowedExtensions.exec(file.name)) {
      alert('파일 형식이 올바르지 않습니다. 엑셀 파일(xlsx, xls, csv)만 업로드 가능합니다.');
      e.target.value = '';
      return;
    }

    // 파일 업로드 시 초기화
    setInitialPer(0);
    setInitialXper(0);
    setFile(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      let totalPer = 0;
      let totalXper = 0;

      // 0번 행(헤더)을 제외한 나머지 데이터에 대해 계산
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        // E 열
        if (row[4]) {
          totalPer += convertDecimalToBigNum(row[4]);
        }
        // F 열
        if (row[5]) {
          totalXper += convertDecimalToBigNum(row[5]);
        }
      }

      setInitialPer(totalPer);
      setInitialXper(totalXper);
    };

    setFile(file);
    reader.readAsArrayBuffer(file);

    // 파일이 선택되면 모달 열기
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setNotificationMessage('엑셀 파일 등록이 취소되었습니다.');
    setShowNotification(true);
    handleModalClose();
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const fetchData = () => {
    if(!file){
      setNotificationMessage('엑셀 파일이 등록되지 않았습니다.');
      setShowNotification(true);
      return;
    }

    const formData = new FormData();
    formData.append('ExcelFile', file);

    for (const key of formData.keys()) {
      console.log(key, ':', formData.get(key));
    }

    setLoading(true);
    axios
        .post(
            `${apiURL}api/PerplayCommunity/PostCreateFromExcel`, // 서버상관 X
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                AccessToken: accessToken,
                'CF-Access-Client-Id': headerData['CF-Access-Client-Id'],
                'CF-Access-Client-Secret': headerData['CF-Access-Client-Secret'],
              },
            },
        )
        .then((response) => {
          console.log('response: ', response);

          setLoading(false);

          if (response?.data?.resultCode === 200) {
            if (response?.data?.failList.length > 0) {
              setFailList(response?.data?.failList);
              setNotificationMessage('우편 발송에 실패했습니다.');
              setShowNotification(true);
              return;
            }
              setNotificationMessage('우편을 발송하였습니다.');
              setShowNotification(true);

              fileInputRef.current.value = '';

              return;
          }

          console.error(response?.data);

          setShowNotification(true);
          setNotificationMessage(getErrorMessage(response?.data));
        })
        .catch((error) => {
          console.error(error);

          setLoading(false);

          setShowNotification(true);
          setNotificationMessage('우편 발송을 실패했습니다. 콘솔로그를 확인하고, 서버에 전송된 데이터를 확인해 주세요');
        });

    fileInputRef.current.value = '';
    setFile(null);

  }

  // -- react hooks --//
  useEffect(() => {
    getUserPageAuth(user).then((res) => setReadOnly(res));
  }, [user]);

  // 우편 발송 버튼 제어
  useEffect(() => {
    if(!file){
      setSubmitPermission(false);
      return;
    }

    if(!address){
      setSubmitPermission(false);
      return;
    }

    console.log("PER")
    console.log(`initialPer : ${initialPer}`);
    console.log(`initialXper: ${initialXper}`);

    if (initialPer >= 0 && initialXper >= 0) {
      console.log("true!");
      setSubmitPermission(true);
      return;
    }

    setSubmitPermission(false);
  }, [address, file, initialPer, initialXper]);

  useEffect(() => {
    setFailList([]);
  }, [file]);

  // -- render --//
  return (
      <div className="page">
        <Helmet>
          <title>{t(title)}</title>
        </Helmet>

        <div className="topSection flowRoot" >
          <div className="leftSection" style={{display : "flex", alignItems : "center"}}>
            <img
                src={BtnIcons.backIcon}
                className="backIcon"
                onClick={() => {
                  window.history.back();
                }}
                alt="Back"
            />
            <p className="titleText">{t(title)}</p>

            <a
                style={{ textDecoration : "none", fontSize : 14 }}
                target={"_blank"}
                rel="noopener noreferrer"
                href={"https://docs.google.com/spreadsheets/d/1jZcJ3dbNGUDAIL21n6f_hXat2lW0JF5puRWaFUWt7Sw/edit?usp=sharing"}
            >
              양식 템플릿으로 이동
            </a>
          </div>

          <div className="rightSectionGap">
            <button
                className="defaultBtn"
                onClick={() => {
                  fetchData();
                }}
                disabled={!submitPermission}
            >
              우편 발송
            </button>
          </div>
        </div>

        <div className="spaceDiv10"/>

        <div className="contentsArea">
          <div className="spaceDiv10" />

          <div className="rowDiv">
            <div className="leftDiv">엑셀 파일<p className="reqVal">*</p></div>
            <div className="rightDiv">
              <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                  disabled={readOnly || address.length === 0}
                  ref={fileInputRef}
              />
            </div>
          </div>

          <div className="spaceDiv10"/>

          <Visible visible={failList.length > 0}>
            <div className="spaceDiv10"/>
            <div className="sectionLine" />
            <div className="spaceDiv10"/>
            <div className="spaceDiv10"/>

            <div className="rowDiv" style={{display : "flex", alignItems : "flex-start"}}>
              <div className="leftDiv">
                  실패 목록
              </div>
              <div className="rightDiv" style={{flexDirection : "column", alignItems : "flex-start"}}>
                <p
                   className={"notice"}
                >
                  <strong style={{paddingLeft : 5}}>{failList.length}개</strong>의 데이터가 잘못 입력되었습니다.
                </p>

                <div className="tableContainer" style={{width: '100%', borderRadius : 5, border : "1px solid #CDCDCD", overflow : "hidden"}}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius : 5}}>
                    <thead>
                    <tr>
                      <th>열</th>
                      <th>계정 번호</th>
                      <th>이메일</th>
                    </tr>
                    </thead>
                    <tbody style={{borderRadius : 5}}>
                    {failList.map((row, index) => (
                        <tr key={index}>
                          <td>{row.row}</td>
                          <td>{row.accountNo}</td>
                          <td>{row.email}</td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="spaceDiv10"/>
            <div className="spaceDiv10"/>
          </Visible>
        </div>

        {/* 로딩 오버레이 */}
        {loading && (
            <div style={{
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "fixed",
              left: 0,
              top: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.2)"
            }}>
              <FetchLoading/>
            </div>
        )}

        {/* 알림 컴포넌트 */}
        {showNotification ? (
            <Notification
                close={() => setShowNotification(false)}
                message={notificationMessage}
            />
        ) : null}

        {/* 엑셀 미리보기 모달 */}
        <ExcelPostModal
            open={isModalOpen}
            onClose={handleModalClose}
            file={file}
            onCancel={handleCancel}
        />
      </div>
  )
}

export default FileBasedPostMain;